import { useState } from "react";
import { Check, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { PLANS, INTERVALS, TRIAL_DAYS } from "@/lib/plans";

const PlanCard = ({ plan, cta, onClick, featured, current, price, per, note, fine }) => (
  <div
    data-testid={`pricing-card-${plan.id}`}
    className={`stone-panel arch-top p-8 flex flex-col ${featured ? "border-[#D99B43]/70 tube-glow" : ""}`}
  >
    <p className="eyebrow text-[#D99B43]">{plan.tagline}</p>
    <h3 className="font-gothic text-2xl uppercase tracking-wider text-amber-100/90 mt-3 flex items-center gap-2">
      {featured && <Crown size={20} className="text-[#D99B43]" />} {plan.name}
    </h3>
    <p className="mt-5" data-testid={`pricing-price-${plan.id}`}>
      <span className="font-gothic text-5xl text-stone-100">{price || plan.price}</span>
      <span className="font-cormorant italic text-xl text-stone-400"> / {per || "month"}</span>
    </p>
    <p className="eyebrow text-[#D99B43] h-4 mt-1" data-testid={`pricing-note-${plan.id}`}>{note}</p>
    <ul className="mt-6 space-y-3 flex-1">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-stone-300">
          <Check size={15} className="text-[#c92a3e] mt-0.5 shrink-0" /> {f}
        </li>
      ))}
    </ul>
    <button
      data-testid={`pricing-cta-${plan.id}`}
      onClick={onClick}
      disabled={current}
      className={`${featured ? "btn-crimson" : "btn-gothic"} mt-8 justify-center w-full disabled:opacity-50`}
    >
      {current ? "Your current plan" : cta}
    </button>
    {fine && <p className="text-[11px] text-stone-500 text-center mt-3" data-testid={`pricing-fine-${plan.id}`}>{fine}</p>}
  </div>
);

export const IntervalSwitch = ({ value, onChange, testId = "billing-interval" }) => (
  <div className="inline-flex border border-[#352d40]" data-testid={`${testId}-switch`}>
    {Object.entries(INTERVALS).map(([k, v]) => (
      <button
        key={k}
        type="button"
        data-testid={`${testId}-${k}`}
        onClick={() => onChange(k)}
        className={`px-4 py-2 font-mono-vox text-[11px] uppercase tracking-[0.15em] transition-colors ${value === k ? "bg-[#8B1E2B] text-stone-100" : "text-stone-400 hover:text-amber-200"}`}
      >
        {v.label}
        {k === "year" && <span className="ml-2 text-[#D99B43]">−17%</span>}
      </button>
    ))}
  </div>
);

export const PricingSection = () => {
  const nav = useNavigate();
  const { user, isPro, startCheckout, trialEligible, billingReady } = useAuth();
  const [interval, setInterval] = useState("month");
  const iv = INTERVALS[interval];
  return (
    <section id="pricing" data-testid="pricing-section" className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
      <p className="eyebrow text-[#D99B43]">✦ Tithes ✦</p>
      <h2 className="font-gothic text-3xl sm:text-4xl uppercase tracking-tight text-amber-100/90 mt-4">Choose your order</h2>
      <p className="font-cormorant italic text-xl text-stone-400 mt-3 max-w-xl">
        Start free. Ascend to Pro for $9 a month, or $90 a year, when your choir outgrows the crypt. Try Pro free for {TRIAL_DAYS} days. Pay by card, Apple Pay, Google Pay or PayPal, cancel anytime.
      </p>
      <div className="mt-8">
        <IntervalSwitch value={interval} onChange={setInterval} />
      </div>
      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <PlanCard plan={PLANS[0]} cta={user ? "Open the studio" : "Start free"} onClick={() => nav(user ? "/library" : "/login")} current={user && !isPro} />
        <PlanCard
          plan={PLANS[1]}
          price={iv.price}
          per={iv.per}
          note={iv.note}
          cta={!billingReady ? "Go Pro" : trialEligible ? `Start ${TRIAL_DAYS}-day free trial` : "Subscribe to Pro"}
          fine={!billingReady ? `${iv.price}/${iv.per} plus applicable tax. Cancel anytime.` : trialEligible ? `Free for ${TRIAL_DAYS} days, then ${iv.price}/${iv.per} plus applicable tax. Cancel anytime.` : `${iv.price}/${iv.per} plus applicable tax. Cancel anytime.`}
          onClick={() => startCheckout(interval)}
          featured
          current={isPro}
        />
      </div>
    </section>
  );
};
